import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import PageIntro from '@/components/generals/page-intro'
import DeckleImage from '@/components/generals/deckle-image'
import Reveal from '@/components/generals/reveal'
import { ghostButton, primaryButton } from '@/components/generals/button-styles'
import { PEOPLE, TIMELINE } from '@/constants/content'

const QUALITIES: Array<{ title: string; text: string }> = [
    {
        title: 'Long fibres, never cut',
        text: 'The water hammer separates mulberry fibres without chopping them, so they stay long and interlock tightly. That is why a sheet this thin is so hard to tear.'
    },
    {
        title: 'Polished, not coated',
        text: 'The smooth surface comes from a stone, not from chemicals. Ink sits on top and dries crisp, with no feathering, even from a broad reed pen.'
    },
    {
        title: 'Nothing bleached',
        text: 'No chlorine, no acid, no wood pulp. Manuscripts on paper made this way have survived for a thousand years, and ours are made to do the same.'
    }
]

const Craft: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='The craft'
                description="The history of Samarkand silk paper, from the Battle of Talas in 751 to its revival, and the family who make it by hand at Qog'oz Atelier today."
            />

            <PageIntro
                id='craft-intro'
                eyebrow='The craft'
                title='Eleven centuries of Samarkand paper'
                intro='A short history of the sheet that once carried the learning of the Islamic world, how it almost disappeared, and the people who make it here today.'
            />

            <section id='craft-images' aria-label='Paper and manuscripts' className='container-page'>
                <div className='grid gap-8 md:grid-cols-12 md:gap-6'>
                    <Reveal className='md:col-span-7'>
                        <DeckleImage
                            src='/images/qogoz/craft-manuscript.jpg'
                            alt='An old manuscript on polished cream paper, a reed pen and a brass ink pot on a walnut table'
                            sizes='(min-width: 768px) 56vw, 94vw'
                            priority
                        />
                    </Reveal>
                    <div className='flex flex-col justify-end md:col-span-4 md:col-start-9'>
                        <Reveal delay={150}>
                            <DeckleImage
                                src='/images/qogoz/step-polishing.jpg'
                                alt='A sheet of paper being burnished with a polished agate stone'
                                sizes='(min-width: 768px) 30vw, 70vw'
                                className='ml-auto w-[70%] md:w-full'
                            />
                            <p className='mt-6 font-heading text-[0.95rem] italic text-bark-soft'>
                                Fig. 3. The agate stone that gives silk paper its name, and its sheen.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section id='history' aria-labelledby='history-title' className='container-page py-20 md:py-32'>
                <Reveal className='max-w-3xl'>
                    <h2 id='history-title' className='font-heading text-[clamp(2.2rem,4.4vw,3.8rem)] font-light leading-[1.04] tracking-[-0.02em] text-bark'>
                        From a battlefield secret to our water wheel
                    </h2>
                </Reveal>

                <ol className='mt-16 border-t border-bark/12 md:mt-20'>
                    {TIMELINE.map((entry, index) => (
                        <Reveal
                            as='li'
                            key={entry.year}
                            className='grid gap-4 border-b border-bark/12 py-10 md:grid-cols-12 md:gap-8 md:py-14'
                        >
                            <p className={cn(
                                'font-heading text-[clamp(2.8rem,6vw,5rem)] font-light leading-none tracking-[-0.03em]',
                                index === TIMELINE.length - 1 ? 'text-pomegranate' : 'text-bark/80',
                                'md:col-span-3'
                            )}>
                                {entry.year}
                            </p>
                            <h3 className={cn('font-heading text-[1.6rem] leading-tight text-bark md:col-span-3', index % 2 === 1 && 'md:col-start-5')}>
                                {entry.title}
                            </h3>
                            <p className={cn('max-w-xl text-[1.04rem] leading-[1.75] text-bark-soft md:col-span-5', index % 2 === 1 ? 'md:col-start-8' : 'md:col-start-7')}>
                                {entry.text}
                            </p>
                        </Reveal>
                    ))}
                </ol>
            </section>

            <section id='qualities' aria-labelledby='qualities-title' className='relative py-4'>
                <div className='torn-edges bg-paper-deep text-bark'>
                    <div className='container-page py-20 md:py-28'>
                        <Reveal>
                            <h2 id='qualities-title' className='max-w-2xl font-heading text-[clamp(2.1rem,4vw,3.4rem)] font-light leading-[1.06] tracking-[-0.02em]'>
                                Why it outlives ordinary paper
                            </h2>
                        </Reveal>
                        <ul className='mt-14 grid gap-12 md:grid-cols-3 md:gap-10'>
                            {QUALITIES.map((quality, index) => (
                                <Reveal as='li' key={quality.title} delay={index * 120} className='border-t border-bark/20 pt-6'>
                                    <p className='text-sm font-semibold tabular-nums text-pomegranate'>{index + 1}</p>
                                    <h3 className='mt-3 font-heading text-[1.6rem] font-light leading-tight'>{quality.title}</h3>
                                    <p className='mt-3 text-[1.02rem] leading-relaxed text-bark-soft'>{quality.text}</p>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section id='people' aria-labelledby='people-title' className='container-page py-20 md:py-32'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-5'>
                        <div className='lg:sticky lg:top-28'>
                            <Reveal>
                                <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                                    <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                                    The people of the workshop
                                </p>
                                <h2 id='people-title' className='mt-6 font-heading text-[clamp(2.2rem,4.2vw,3.5rem)] font-light leading-[1.04] tracking-[-0.02em] text-bark'>
                                    One family, one water wheel
                                </h2>
                            </Reveal>
                            <Reveal delay={150} className='mt-10'>
                                <DeckleImage
                                    src='/images/qogoz/people-master.jpg'
                                    alt='The master papermaker in an indigo work coat standing in the doorway of the workshop'
                                    sizes='(min-width: 1024px) 36vw, 80vw'
                                    className='w-[85%] sm:w-[60%] lg:w-[85%]'
                                />
                                <p className='mt-6 font-heading text-[0.95rem] italic text-bark-soft'>
                                    Bakhtiyor at the door of the mill.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <ul className='lg:col-span-6 lg:col-start-7 lg:pt-40'>
                        {PEOPLE.map((person, index) => (
                            <Reveal as='li' key={person.name} delay={index * 80} className='border-t border-bark/12 py-10 first:border-t-0 first:pt-0 lg:first:border-t lg:first:pt-10'>
                                <p className='text-sm text-bark-soft'>{person.role}</p>
                                <h3 className='mt-3 font-heading text-[2rem] font-light leading-tight text-bark'>{person.name}</h3>
                                <p className='mt-4 max-w-lg text-[1.04rem] leading-[1.75] text-bark-soft'>{person.text}</p>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='craft-visit' aria-labelledby='craft-visit-title' className='border-t border-bark/10 bg-paper-deep/50'>
                <div className='container-page flex flex-col gap-8 py-16 md:flex-row md:items-end md:justify-between md:py-24'>
                    <Reveal className='max-w-2xl'>
                        <h2 id='craft-visit-title' className='font-heading text-[clamp(2rem,4vw,3.2rem)] font-light leading-[1.06] text-bark'>
                            The best way to understand it is to hold a sheet.
                        </h2>
                    </Reveal>
                    <Reveal delay={120} className='flex flex-wrap gap-3'>
                        <Link href='/workshops' className={primaryButton}>
                            Visit the workshop
                            <ArrowRight className='size-4' strokeWidth={1.75} />
                        </Link>
                        <Link href='/shop' className={ghostButton}>
                            Shop the paper
                        </Link>
                    </Reveal>
                </div>
            </section>
        </Layout>
    )
}

export default Craft
